import React, { useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'

import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'

import { useAuth } from '@sputnik/auth'
import UploaderTileList from '@sputnik/components/UploaderTileList/UploaderTileList'
import useExpiration from '@sputnik/hooks/useExpiration'
import GetRequiredDocumentsMobileUpload from '@sputnik/pages/gql/GetRequiredDocumentsMobileUpload.gql'
import { logInfo } from '@sputnik/utils/loggerUtils'

import { documentUploadPageWrapperCss } from './DocumentUploadStyles'
import MobileDocumentUploadStatus from './MobileDocumentUploadStatus'

// container to the mobile-document-upload page
const UploadContent = () => {
  const [remainingDocs, setRemainingDocs] = useState([])
  const onSetRemainingDocs = useEffectEvent((documents) => {
    setRemainingDocs(documents.map((document) => document?.documentType))
  })
  const milliSecondsToExpiry = new Date(new Date().getTime() + 15 * 60 * 1000)
  const isExpired = useExpiration(milliSecondsToExpiry)

  const { getCreditAppIdOnToken } = useAuth()
  const salesforceCreditId = getCreditAppIdOnToken()

  useDidMount(() =>
    logInfo('qaMobileDocumentUploadAuth', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: 'User authenticated with mobile document upload',
      },
      eventName: 'MobileDeviceQrDocumentUpload',
    }),
  )

  const { data, loading, error } = useQuery(GetRequiredDocumentsMobileUpload, {
    fetchPolicy: 'network-only',
    variables: {
      creditApplicationId: salesforceCreditId,
    },
  })

  const creditApp = data?.creditApplication
  const documents = creditApp?.requiredDocs

  const requiredDocuments = useMemo(() => (Array.isArray(documents) ? documents : []), [documents])
  useEffect(() => {
    requiredDocuments && onSetRemainingDocs(requiredDocuments)
  }, [requiredDocuments])

  if (remainingDocs.length === 0 && loading === false) {
    return <MobileDocumentUploadStatus page='complete' />
  }

  if (isExpired) {
    return <MobileDocumentUploadStatus page='invalidLink' />
  }

  if (error) {
    return <MobileDocumentUploadStatus page='error' />
  }

  return (
    <div
      className='fs-unmask'
      css={documentUploadPageWrapperCss}
      data-testid='mobile-document-upload-container'
    >
      <div className='page-area'>
        <div className='main-header'>
          <div className='main-header-text'>
            <div className='main-title'>
              <FormattedMessage
                defaultMessage='Let’s get you verified!'
                id='sputnik.UploadContent__xQCTP/'
              />
            </div>
            <div className='main-caption'>
              <FormattedMessage
                defaultMessage='Please make sure your document is clean, clear, and readable.  Select the document you would like to upload.'
                id='sputnik.UploadContent__AgypUS'
              />
            </div>
          </div>
        </div>
        <div className='page-content'>
          <UploaderTileList
            creditApp={creditApp}
            remainingDocs={remainingDocs}
            requiredDocuments={requiredDocuments}
            setRemainingDocs={setRemainingDocs}
          />
        </div>
      </div>
    </div>
  )
}

export default UploadContent
